import * as React from 'react';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { FREE_TRIAL_DOMAIN_LIMIT } from '@/lib/trial';
import { cn } from '@/lib/utils';
import { AlertCircle, Clock, ShieldCheck } from 'lucide-react';

type StatusContext = 'domain' | 'invoice';

type StatusStyle = {
  label: string;
  className?: string;
  variant?: 'default' | 'secondary' | 'destructive' | 'outline' | 'neutral' | 'success' | 'warning' | 'danger' | 'info';
  icon?: React.ComponentType<{ className?: string }>;
  tooltip?: string;
};

const DOMAIN_STATUS_CONFIG: Record<string, StatusStyle> = {
  active: {
    label: 'Protected',
    className: 'gap-1 cursor-pointer',
    variant: 'success',
    icon: ShieldCheck,
    tooltip: 'Your site has active ShadowScan protection and an active subscription state.',
  },
  pending: {
    label: 'Pending Setup',
    className: 'gap-1 cursor-pointer',
    variant: 'warning',
    icon: Clock,
    tooltip: 'Setup is still in progress. Finish pairing and billing setup to activate protection.',
  },
  trial: {
    label: 'Trial Active',
    className: 'gap-1 cursor-pointer',
    variant: 'info',
    icon: ShieldCheck,
    tooltip: `Your 7-day free trial is active for this domain. The trial is capped at up to ${FREE_TRIAL_DOMAIN_LIMIT} eligible self-serve domains per account, and billing starts only after the trial ends.`,
  },
  payment_needed: {
    label: 'Billing paused',
    className: 'gap-1 cursor-pointer',
    variant: 'warning',
    icon: AlertCircle,
    tooltip: 'Protection remains active. If billing is not resumed within 7 days, automated updates and configuration changes are temporarily paused until billing resumes.',
  },
  payment_incomplete: {
    label: 'Billing paused',
    className: 'gap-1 cursor-pointer',
    variant: 'warning',
    icon: AlertCircle,
    tooltip: 'Protection remains active. If billing is not resumed within 7 days, automated updates and configuration changes are temporarily paused until billing resumes.',
  },
  pending_payment: {
    label: 'Billing paused',
    className: 'gap-1 cursor-pointer',
    variant: 'warning',
    icon: AlertCircle,
    tooltip: 'Protection remains active. If billing is not resumed within 7 days, automated updates and configuration changes are temporarily paused until billing resumes.',
  },
  deleted: {
    label: 'Removed',
    className: 'gap-1 cursor-pointer',
    variant: 'danger',
    icon: AlertCircle,
    tooltip: 'This site has been removed and can be restored by an administrator if needed.',
  },
};

const INVOICE_STATUS_CONFIG: Record<string, StatusStyle> = {
  paid: {
    label: 'Paid',
    variant: 'success',
  },
  open: {
    label: 'Open',
    variant: 'warning',
  },
  draft: {
    label: 'Draft',
    variant: 'neutral',
  },
  void: {
    label: 'Void',
    variant: 'danger',
  },
};

const formatLabel = (status: string) =>
  status.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

type StatusBadgeProps = {
  status: string;
  context?: StatusContext;
  className?: string;
  tooltip?: string;
};

export function StatusBadge({
  status,
  context = 'domain',
  className,
  tooltip,
}: StatusBadgeProps) {
  const normalized = status?.toLowerCase?.() ?? '';
  const config =
    (context === 'invoice' ? INVOICE_STATUS_CONFIG[normalized] : DOMAIN_STATUS_CONFIG[normalized]) ??
    ({
      label: formatLabel(status || 'Unknown'),
      variant: 'neutral',
      tooltip: status ? `Status: ${formatLabel(status)}` : undefined,
    } satisfies StatusStyle);

  const badgeContent = (
    <Badge
      variant={config.variant}
      className={cn('flex items-center', config.className, className)}
    >
      {config.icon && <config.icon className="h-3 w-3" />}
      {config.label}
    </Badge>
  );

  const tooltipContent = tooltip || config.tooltip;

  if (!tooltipContent) {
    return badgeContent;
  }

  return (
    <Popover>
      <PopoverTrigger asChild>{badgeContent}</PopoverTrigger>
      <PopoverContent className="w-fit max-w-xs px-3 py-2 text-sm">
        {tooltipContent}
      </PopoverContent>
    </Popover>
  );
}
